/* eslint-disable no-param-reassign */
import { findAccessor } from './dom';
const getTime = typeof window === 'object' && window.performance?.now
    ? () => window.performance.now()
    : () => new Date().getTime();
export function isScrollable(element) {
    const style = window.getComputedStyle(element);
    const allowlist = ['auto', 'scroll'];
    return (allowlist.includes(style.overflowX) || allowlist.includes(style.overflowY));
}
export class InterruptedError extends Error {
}
// Store the currently scrolling elements and their cancelers, so that we can
// cancel the previous animation if we triggered a new scroll on the same element.
const scrollCancellerMap = new Map();
/**
 * Scroll an element into target position. Similar to `scrollTo` or `scrollBy`
 * method, but it scrolls the container only and does not infect the accessors.
 *
 * @param container The container element to be scrolled.
 * @param options Options for the scroll.
 * @returns A promise that resolves when the scroll is done (animation ends),
 *          or rejects if the scroll is interrupted by the user or another
 *          scroll on the same element.
 */
export async function scrollContainer(container, opt = {}) {
    await new Promise((resolve, reject) => {
        const { scrollLeft, scrollTop } = container;
        const getX = opt.getX ??
            (opt.deltaX !== undefined
                ? () => scrollLeft + opt.deltaX
                : undefined) ??
            (opt.x !== undefined ? () => opt.x : undefined);
        const getY = opt.getY ??
            (opt.deltaY !== undefined
                ? () => scrollTop + opt.deltaY
                : undefined) ??
            (opt.y !== undefined ? () => opt.y : undefined);
        if (!getX && !getY) {
            resolve();
            return;
        }
        let animation;
        function getDirectionDistance(base, target) {
            if (!target) {
                return 0;
            }
            return Math.abs(target() - base);
        }
        const distance = Math.sqrt(getDirectionDistance(scrollLeft, getX) ** 2 +
            getDirectionDistance(scrollTop, getY) ** 2);
        // Default duration is based on the distance
        const duration = opt.animated
            ? opt.animationDuration ?? distance ** 0.15 * 120
            : 0;
        const startTime = getTime();
        function interpolate(baseValue, targetValue, progress) {
            if (progress <= 0) {
                return baseValue;
            }
            if (progress >= 1) {
                return targetValue;
            }
            const percentage = (1 - Math.cos(progress ** 0.7 * Math.PI)) / 2;
            return (targetValue - baseValue) * percentage + baseValue;
        }
        function handleAbort() {
            if (animation) {
                window.cancelAnimationFrame(animation);
                animation = undefined;
            }
            reject(new InterruptedError('The scroll is interrupted by the user or another scroll.'));
            // eslint-disable-next-line no-use-before-define
            postScroll();
        }
        function postScroll() {
            container.removeEventListener('mousedown', handleAbort);
            container.removeEventListener('touchstart', handleAbort);
            container.removeEventListener('keydown', handleAbort);
            container.removeEventListener('wheel', handleAbort);
            scrollCancellerMap.delete(container);
            void Promise.resolve().then(() => {
                resolve();
            });
        }
        function loop() {
            const progress = duration ? (getTime() - startTime) / duration : 1;
            let inProgress = false;
            if (getX) {
                const x = getX();
                if (Math.abs(container.scrollLeft - x) < 1) {
                    container.scrollLeft = x;
                }
                else {
                    inProgress = true;
                    container.scrollLeft = interpolate(scrollLeft, x, progress);
                }
            }
            if (getY) {
                const y = getY();
                if (Math.abs(container.scrollTop - y) < 1) {
                    container.scrollTop = y;
                }
                else {
                    inProgress = true;
                    container.scrollTop = interpolate(scrollTop, y, progress);
                }
            }
            if (progress < 1 && inProgress) {
                animation = window.requestAnimationFrame(loop);
            }
            else {
                postScroll();
            }
        }
        if (scrollCancellerMap.has(container)) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            scrollCancellerMap.get(container)();
        }
        scrollCancellerMap.set(container, handleAbort);
        if (opt.allowUserInterrupt) {
            container.addEventListener('mousedown', handleAbort);
            container.addEventListener('touchstart', handleAbort);
            container.addEventListener('keydown', handleAbort);
            container.addEventListener('wheel', handleAbort);
        }
        loop();
    });
}
/**
 * Scroll an element into container's view. Similar to `scrollIntoView` method,
 * but it scrolls the container only and does not infect the accessors.
 *
 * A params for margin around element is also available.
 *
 * @param child The child element to be scrolled into view.
 * @param options Options for the scroll.
 * @returns A promise that resolves when the scroll is done (animation ends),
 *          or rejects if the scroll is interrupted by the user or another
 *          scroll on the same element.
 */
export async function scrollChildIntoContainer(child, opt = {}) {
    const container = opt.container ??
        findAccessor(child, (ele) => ele !== child && isScrollable(ele)) ??
        document.scrollingElement ??
        document.body;
    const { margin = 0 } = opt;
    const marginArray = Array.isArray(opt.margin)
        ? (() => {
            const m = margin;
            switch (m.length) {
                case 0:
                    return [0, 0, 0, 0];
                case 1:
                    return [m[0], m[0], m[0], m[0]];
                case 2:
                    return [m[0], m[1], m[0], m[1]];
                case 3:
                    return [m[0], m[1], m[2], m[1]];
                default:
                    return m;
            }
        })()
        : [margin, margin, margin, margin];
    function positionGetter(direction, alignConfig) {
        const horizontal = direction === 'x';
        function getEndpoint(box, applyMargin) {
            const result = horizontal
                ? [box.left, box.right]
                : [box.top, box.bottom];
            if (applyMargin) {
                if (horizontal) {
                    result[0] -= marginArray[3];
                    result[1] += marginArray[1];
                }
                else {
                    result[0] -= marginArray[0];
                    result[1] += marginArray[2];
                }
            }
            return result;
        }
        function getAlignTarget([start, end], align) {
            switch (align) {
                case 'start':
                    return start;
                case 'end':
                    return end;
                case 'center':
                    return (start + end) / 2;
                default:
                    return 0;
            }
        }
        return () => {
            const parentRect = container === document.documentElement || container === document.body
                ? {
                    left: 0,
                    top: 0,
                    right: window.innerWidth,
                    bottom: window.innerHeight,
                }
                : container.getBoundingClientRect();
            const outerEndpoint = getEndpoint(parentRect);
            const innerEndpoint = getEndpoint(child.getBoundingClientRect(), true);
            const align = alignConfig === true ||
                alignConfig === undefined ||
                alignConfig === 'nearest'
                ? (() => {
                    // Nearest, or by default
                    if (innerEndpoint[0] < outerEndpoint[0]) {
                        return 'start';
                    }
                    if (innerEndpoint[1] > outerEndpoint[1]) {
                        return 'end';
                    }
                    return undefined;
                })()
                : alignConfig;
            const parentTarget = getAlignTarget(outerEndpoint, align);
            const childTarget = getAlignTarget(innerEndpoint, align);
            const delta = childTarget - parentTarget;
            return (horizontal ? container.scrollLeft : container.scrollTop) + delta;
        };
    }
    await scrollContainer(container, {
        getX: opt.x !== false ? positionGetter('x', opt.x) : undefined,
        getY: opt.y !== false ? positionGetter('y', opt.y) : undefined,
        animated: opt.animated,
        animationDuration: opt.animationDuration,
        allowUserInterrupt: opt.allowUserInterrupt,
    });
}
